import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { Mic, Play, Clock, ChevronRight, Sparkles, Loader2, CheckCircle, RotateCcw, Timer } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const CATEGORY_CONFIG = {
  behavioral: { label: 'Behavioral', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  academic: { label: 'Academic', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  situational: { label: 'Situational', color: 'bg-amber-50 text-amber-700 border-amber-200' },
};

function TimerDisplay({ running, onReset }) {
  const [seconds, setSeconds] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const reset = () => { setSeconds(0); onReset?.(); };
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  const isLong = seconds > 120;

  return (
    <div className="flex items-center gap-2">
      <Timer className={`h-3.5 w-3.5 ${isLong ? 'text-amber-500' : 'text-muted-foreground'}`} />
      <span className={`text-sm font-mono ${isLong ? 'text-amber-500' : 'text-muted-foreground'}`}>
        {mins}:{secs.toString().padStart(2, '0')}
      </span>
      {seconds > 0 && (
        <button onClick={reset} className="text-muted-foreground hover:text-foreground">
          <RotateCcw className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function ScoreBadge({ score }) {
  const color = score >= 8 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : score >= 6 ? 'bg-blue-50 text-blue-700 border-blue-200'
    : score >= 4 ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-red-50 text-red-700 border-red-200';
  return <Badge className={`${color} text-xs`}>{score}/10</Badge>;
}

export default function InterviewPrepPage() {
  const [questions, setQuestions] = useState({});
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeQ, setActiveQ] = useState(null);
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [timerRunning, setTimerRunning] = useState(false);
  const [activeCategory, setActiveCategory] = useState('behavioral');

  const load = useCallback(() => {
    Promise.all([
      api.getInterviewQuestions(),
      api.getInterviewHistory(),
    ]).then(([q, h]) => {
      setQuestions(q.questions || {});
      setHistory(h.history || []);
    }).catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const practicedIds = new Set(history.map(h => h.question_id));

  const handleSelectQuestion = (q, category) => {
    setActiveQ({ ...q, category });
    setAnswer('');
    setFeedback(null);
    setTimerRunning(true);
  };

  const handleSubmit = async () => {
    if (!answer.trim()) { toast.error('Write your answer first'); return; }
    setSubmitting(true);
    setTimerRunning(false);
    try {
      const result = await api.getInterviewFeedback({
        question_id: activeQ.id,
        question: activeQ.question,
        answer: answer,
        category: activeQ.category,
      });
      setFeedback(result);
      toast.success('Feedback received');
      load();
    } catch (err) { toast.error(err.message || 'Feedback failed'); }
    finally { setSubmitting(false); }
  };

  const avgScore = history.length ? (history.reduce((s, h) => s + (h.score || 0), 0) / history.length).toFixed(1) : 0;
  const categoryQuestions = questions[activeCategory] || [];

  if (loading) return (
    <div className="max-w-3xl mx-auto space-y-3 animate-pulse">
      {[1,2,3].map(i => <div key={i} className="h-16 bg-[#E2E2E2] rounded-xl" />)}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="interview-prep-page">
      <div>
        <h1 className="text-2xl font-medium tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
          Interview Prep
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          {history.length} questions practiced · Avg score: {avgScore}/10 · {practicedIds.size} unique questions
        </p>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-1 bg-[#F9F9F7] p-1 rounded-lg" data-testid="interview-tabs">
        {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
          <button key={key} onClick={() => { setActiveCategory(key); setActiveQ(null); setFeedback(null); }}
            className={`flex-1 px-3 py-2 text-xs font-medium rounded-md transition-all text-center
              ${activeCategory === key ? 'bg-white text-[#0F392B] shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid={`tab-${key}`}>
            {cfg.label} ({(questions[key] || []).length})
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Question List */}
        <div className="space-y-2">
          <h2 className="text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Questions</h2>
          {categoryQuestions.map(q => (
            <Card key={q.id}
              className={`border cursor-pointer transition-all hover:shadow-sm ${activeQ?.id === q.id ? 'border-[#0F392B]/40 bg-[#0F392B]/[0.02]' : 'border-[#E2E2E2]'}`}
              onClick={() => handleSelectQuestion(q, activeCategory)}
              data-testid={`question-${q.id}`}>
              <CardContent className="p-3.5 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{q.question}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{q.tip}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {practicedIds.has(q.id) && <CheckCircle className="h-4 w-4 text-emerald-500" />}
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Practice Area */}
        <div className="space-y-4">
          {activeQ ? (
            <>
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Practice</h2>
                <TimerDisplay running={timerRunning} onReset={() => setTimerRunning(false)} />
              </div>

              <Card className="border-[#0F392B]/20">
                <CardContent className="p-4 space-y-4">
                  <div>
                    <Badge variant="outline" className={`text-[10px] ${CATEGORY_CONFIG[activeQ.category]?.color}`}>
                      {CATEGORY_CONFIG[activeQ.category]?.label}
                    </Badge>
                    <p className="text-sm font-medium mt-2">{activeQ.question}</p>
                    <p className="text-[10px] text-[#D4AF37] mt-1">{activeQ.tip}</p>
                  </div>

                  <Textarea value={answer} onChange={e => setAnswer(e.target.value)}
                    rows={8} placeholder="Type your answer here... Aim for 60-90 seconds of speaking time (about 150-200 words)."
                    className="text-sm resize-none" data-testid="interview-answer-input" />

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{answer.trim().split(/\s+/).filter(Boolean).length} words</span>
                    <Button onClick={handleSubmit} disabled={submitting || !answer.trim()}
                      className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white text-xs h-8"
                      data-testid="submit-answer-btn">
                      {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                      Get AI Feedback
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {feedback && (
                <Card className="border-[#D4AF37]/20 bg-[#FAFAF8]" data-testid="interview-feedback-result">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                        <span className="text-xs font-semibold">AI Interview Coach</span>
                      </div>
                      <ScoreBadge score={feedback.score} />
                    </div>
                    <div className="text-xs leading-relaxed whitespace-pre-wrap">{feedback.feedback}</div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <Mic className="h-12 w-12 text-muted-foreground/20 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-muted-foreground">Select a question to start practicing</p>
              <p className="text-xs text-muted-foreground mt-1">AI will score your answer and give actionable feedback</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
